import { useSnackbar } from "notistack";
import { APIsContext } from "../../Store/APIs/context";
import { useContext, useState } from "react";
import { Dialog, DialogTitle, DialogContent, TextField, DialogActions, MenuItem, Select } from "@mui/material";
import { Box, styled } from "@mui/system";
import ButtonGradient from "../Filter/Components/ButtonGradient";
import ButtonLightGray from "../Filter/Components/ButtonLightGray";
import GridContainer from "../Filter/Components/grid/GridContainer";
import DialogTitleClose from "../Misc/Dialog/Components/DialogTitleClose";
import { AccountModel, ChangeAccountRequest } from "neurotec-megamatcherid-management-client";
import { AccountRole } from "../../Store/Accounts/types";
import DialogTypographyTitle from "../Misc/Dialog/Components/DialogTypographyTitle";

const SelectType = styled(Select)({
    padding: "none",
})

interface IChangeAccountDialogProps {
    account: AccountModel | undefined
    open: boolean,
    setOpen: (open: boolean) => void
    rerender: () => {}
}

const ChangeAccountDialog: React.FC<IChangeAccountDialogProps> = (props) => {

    const { enqueueSnackbar } = useSnackbar();
    const apisState = useContext(APIsContext).state
    const [usernameError, setUsernameError] = useState(false)
    const [passwordError, setPasswordError] = useState(false)
    const [newPasswordError, setNewPasswordError] = useState(false)

    const handleOnClose = () => {
        props.setOpen(false)
    }

    const handleExit = () => {
        setUsernameError(false)
        setPasswordError(false)
    }

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        const username = new FormData(event.currentTarget).get("account-username") as string;
        const password = new FormData(event.currentTarget).get("account-old-password") as string;
        const newPassword = new FormData(event.currentTarget).get("account-new-password") as string;
        const newPasswordConfirm = new FormData(event.currentTarget).get("account-new-password-confirm") as string;
        const role = new FormData(event.currentTarget).get("account-role") as AccountRole;

        if ((username === null || username === "")) {
            enqueueSnackbar("Username must not be blank", {variant: "warning"})
            setUsernameError(true)
            return
        } else {
            setUsernameError(false)
        }

        if (props.account?.role === AccountRole.ADMIN && (password === null || password === "")) {
            enqueueSnackbar("Old password must not be blank", {variant: "warning"})
            setPasswordError(true)
            return
        } else {
            setPasswordError(false)
        }

        if ((newPassword !== null && newPassword.length > 0) && (newPasswordConfirm !== null && newPasswordConfirm.length > 0)) {
            if (newPassword !== newPasswordConfirm) {
                enqueueSnackbar("New passwords must match", {variant: "warning"})
                setNewPasswordError(true)
                return
            } else {
                setNewPasswordError(false)
            }
        }

        const request: ChangeAccountRequest = {
            username: props.account?.username,
            newUsername: username,
            password: password,
            newPassword: newPassword,
            newRole: role
        }

        apisState.AccountsApi.changeAccount({changeAccountRequest: request}).then(res => {
            if (res.status === 200) {
                enqueueSnackbar("Successfully changed account", {variant: "success"})
                props.rerender()
            } else {
                enqueueSnackbar("Failed to change account", {variant: "error"})
            }
        }).catch(err => {
            console.log(err)
            enqueueSnackbar(err.message, {variant: "error"})
            return
        })
        handleOnClose()
    }

    return (
        <Dialog
            maxWidth="xs"
            scroll="body"
            fullWidth
            open={props.open}
            onClose={handleOnClose}
            onTransitionExited={handleExit}
        >
            <Box component="form" onSubmit={handleSubmit} noValidate >
                <DialogTitle>
                    <DialogTitleClose title={"Change account"} onClose={handleOnClose}/>
                </DialogTitle>
                <DialogContent style={{paddingBottom: "0px", paddingLeft: "0px", paddingRight: "0px"}}>
                    <GridContainer container style={{paddingTop: "0.5rem", paddingRight: "2rem", paddingLeft: "2rem"}}>
                        <DialogTypographyTitle>Username*</DialogTypographyTitle>
                        <TextField
                            defaultValue={props.account?.username}
                            sx={{margin: "none"}}
                            style={{paddingBottom: "0.5rem"}}
                            autoComplete="one-time-code"
                            id="account-username"
                            name="account-username"
                            placeholder="Username"
                            inputProps={{
                                style: {paddingBottom: "0.5rem"}
                            }}
                            InputLabelProps={{
                                sx: {top: "-7px", "&.MuiInputLabel-shrink": {top: "0px"}},
                            }}
                            error={usernameError}
                        />
                        {props.account?.role === AccountRole.ADMIN? 
                           <>
                           <DialogTypographyTitle>Old password*</DialogTypographyTitle>
                           <TextField
                                sx={{margin: "none"}}
                                style={{paddingBottom: "0.5rem"}}
                                autoComplete="one-time-code"
                                id="account-old-password"
                                name="account-old-password"
                                placeholder="Old password"
                                type="password"
                                inputProps={{
                                    style: {paddingBottom: "0.5rem"}
                                }}
                                InputLabelProps={{
                                    sx: {top: "-7px", "&.MuiInputLabel-shrink": {top: "0px"}},
                                }}
                                error={passwordError}
                            />
                            </> : null
                        }
                        <DialogTypographyTitle>New password</DialogTypographyTitle>
                        <TextField
                            sx={{margin: "none"}}
                            style={{paddingBottom: "0.5rem"}}
                            autoComplete="one-time-code"
                            id="account-new-password"
                            name="account-new-password"
                            placeholder="New password"
                            type="password"
                            inputProps={{
                                style: {paddingBottom: "0.5rem"}
                            }}
                            InputLabelProps={{
                                sx: {top: "-7px", "&.MuiInputLabel-shrink": {top: "0px"}},
                            }}
                            error={newPasswordError}
                        />
                        <DialogTypographyTitle>Confirm new password</DialogTypographyTitle>
                        <TextField
                            sx={{margin: "none"}}
                            style={{paddingBottom: "0.5rem"}}
                            autoComplete="one-time-code"
                            id="account-new-password-confirm"
                            name="account-new-password-confirm"
                            placeholder="New password confirm"
                            type="password"
                            inputProps={{
                                style: {paddingBottom: "0.5rem"}
                            }}
                            InputLabelProps={{
                                sx: {top: "-7px", "&.MuiInputLabel-shrink": {top: "0px"}},
                            }}
                            error={newPasswordError}
                        />
                        <DialogTypographyTitle>Role*</DialogTypographyTitle>
                        <SelectType
                            id={"account-role"}
                            name={"account-role"}
                            defaultValue={props.account?.role}
                            style={{width: "100%", marginBottom: "0.75rem"}}
                            MenuProps={{MenuListProps: {disablePadding: true}}}
                        >
                            <MenuItem value={AccountRole.ADMIN}>Admin</MenuItem>
                            <MenuItem value={AccountRole.USER}>User</MenuItem>
                        </SelectType>
                    </GridContainer>

                </DialogContent>
                <DialogActions>
                    <ButtonLightGray onClick={handleOnClose}>
                        Cancel
                    </ButtonLightGray>
                    <ButtonGradient variant="contained" color="primary" type='submit'>
                        Change account
                    </ButtonGradient>
                </DialogActions>
            </Box>
        </Dialog>
    )
}

export default ChangeAccountDialog